// Los textos del módulo, en castellano.
//
// Viajan CON el módulo, en los cuatro idiomas, incluidas las claves de las
// pantallas de la plataforma que hablan de él —su nombre en el menú, sus
// permisos en Roles, sus ajustes, sus acciones en Auditoría y sus arranques de
// conversación en el asistente—. Un módulo que obliga a añadir claves al shell
// no es extraíble (§4.2), y el test `platform/web/tests/i18nCoverage.test.js`
// exige que los cuatro ficheros tengan EXACTAMENTE el mismo juego de claves.
//
// Dos normas de escritura que no son de estilo:
//   · La arroba se escribe {'@'}: vue-i18n la lee como enlace a otra clave y un
//     mensaje que no compila desaparece de la pantalla sin decir nada.
//   · Las interpolaciones son las mismas en los cuatro idiomas. Si aquí hay
//     {count}, allí también.

export default {
  markets: {
    title: 'Mercados',

    actions: {
      save: 'Guardar',
      saved: 'Guardado',
      cancel: 'Cancelar',
      edit: 'Editar',
      remove: 'Quitar',
    },

    units: {
      points: 'p.',
    },

    windows: {
      day: 'Un día',
      week: 'Una semana',
      month: '30 días',
      year: 'Un año',
    },

    // ── El panel de inicio ───────────────────────────────────────────────────
    home: {
      title: 'Mercados',
      subtitle: 'Qué está cambiando fuera y cuánto os cuesta',
      prepareMeeting: 'Preparar reunión',
      risks: 'Riesgos abiertos',
      opportunities: 'Oportunidades',
      watched: 'Factores vigilados',
      newReports: 'Informes nuevos',
      exposure: 'Compra expuesta',
      costAtRisk: 'Sobrecoste anual',
      whatChanged: 'Qué ha cambiado',
      summarise: 'Resumir con IA',
      summaryUnavailable: 'El resumen no está disponible ahora mismo. Los cambios de abajo siguen siendo los mismos.',
      nothingChanged: 'Nada se ha movido',
      nothingChangedHint: 'Ningún factor vigilado ha cruzado su umbral desde la última vez que entraste.',
      references: '{count} referencias',
      radar: 'Lo que más se mueve',
      radarQuiet: 'Ningún factor vigilado está fuera de lo normal.',
      unlinked: '{count} indicadores no están atados a nada vuestro: de esos no sale ninguna cifra en euros.',
      stale: '{count} indicadores llevan tiempo sin actualizarse.',
      toDo: 'Pendiente de alguien',
      sourcesDue: 'Fuentes por revisar',
      decisionsDue: 'Decisiones por valorar',
      overdue: '{count} días de retraso',
      nothingToDo: 'No hay nada pendiente.',
      reports: 'Informes recientes',
      kind: {
        impact: 'Impacto',
        indicator: 'Indicador',
        report: 'Informe',
      },
    },

    coverage: {
      title: 'Todavía no hay datos internos',
      body: 'Este módulo cruza lo que pasa fuera con vuestro consumo, vuestras compras y vuestros costes. Sin Almacén ni Compras instalados no hay con qué cruzarlo.',
      hint: 'Lo de fuera —temas, fuentes, informes e indicadores— funciona igual desde hoy.',
    },

    // ── Radar ────────────────────────────────────────────────────────────────
    radar: {
      title: 'Radar',
      subtitle: 'Todo lo que vigiláis, con su color',
      recompute: 'Recalcular ahora',
      recomputed: 'Recalculado: {count} impactos',
      colFactor: 'Factor',
      colValue: 'Valor',
      colChange: 'Variación',
      colStatus: 'Estado',
      colCost: 'Coste anual',
      noLinks: 'Sin enlazar',
      notUpdated: 'Sin actualizar',
      unlinked: '{count} sin enlazar con nada vuestro',
      stale: '{count} sin actualizar',
      empty: 'El radar está vacío',
      emptyHint: 'Da de alta un indicador y pega su serie para que empiece a vigilarse.',
    },

    status: {
      favourable: 'Favorable',
      neutral: 'Normal',
      watch: 'Atención',
      risk: 'Riesgo',
      critical: 'Crítico',
      unknown: 'Sin datos',
      up: 'Sube',
      down: 'Baja',
      flat: 'Estable',
    },

    severity: {
      info: 'Información',
      watch: 'Atención',
      high: 'Importante',
      critical: 'Crítico',
    },

    direction: {
      risk: 'Riesgo',
      opportunity: 'Oportunidad',
      both: 'Riesgos y oportunidades',
    },

    trend: {
      up: 'Al alza',
      down: 'A la baja',
      flat: 'Estable',
      unknown: 'Sin serie suficiente',
    },

    kinds: {
      commodity: 'Materias primas',
      energy: 'Energía',
      logistics: 'Transporte y logística',
      industry: 'Actividad industrial',
      construction: 'Construcción',
      economy: 'Economía',
      fx: 'Divisas',
      labour: 'Mano de obra',
      sector: 'Sector',
    },

    valueTypes: {
      price: 'Precio',
      index: 'Índice',
      rate: 'Tasa o porcentaje',
      quantity: 'Cantidad',
    },

    badDirection: {
      up: 'Subir nos perjudica',
      down: 'Bajar nos perjudica',
      none: 'Ninguna dirección nos perjudica',
    },

    origins: {
      manual: 'A mano',
      import: 'Pegado',
      report: 'De un informe',
      feed: 'Automático',
    },

    horizons: {
      short: 'Corto plazo',
      medium: 'Medio plazo',
      long: 'Largo plazo',
      unknown: 'Sin plazo',
    },

    importance: {
      1: 'Baja',
      2: 'Media',
      3: 'Alta',
    },

    strength: {
      none: 'ninguna',
      weak: 'débil',
      moderate: 'moderada',
      strong: 'fuerte',
    },

    confidence: {
      high: 'Confianza alta',
      medium: 'Confianza media',
      low: 'Confianza baja',
      short: 'Confianza {score}',
    },

    metrics: {
      revenue: 'Facturación',
      order_intake: 'Trabajo ganado',
      offers_out: 'Presupuestado',
      purchase_spend: 'Compra recibida',
      material_cost: 'Material consumido',
    },

    tiers: {
      A: 'Organismo oficial: INE, Eurostat, BCE, Comisión Europea',
      B: 'Asociación sectorial o consultora reconocida',
      C: 'Prensa especializada',
      D: 'Sin verificar',
    },

    sourceKinds: {
      website: 'Página web',
      rss: 'RSS',
      pdf: 'PDF',
      report: 'Informe',
      api: 'API',
      spreadsheet: 'Hoja de cálculo',
      newsletter: 'Boletín',
      association: 'Asociación sectorial',
      public_body: 'Organismo público',
      supplier: 'Proveedor',
      institute: 'Instituto económico',
      bank: 'Banco',
      consultancy: 'Consultora',
      manual: 'A mano',
    },

    // ── Indicadores y materias primas ────────────────────────────────────────
    indicators: {
      title: 'Indicadores',
      subtitle: 'Lo que vigiláis de fuera, con su serie',
      new: 'Nuevo indicador',
      edit: 'Editar indicador',
      searchPlaceholder: 'Nombre o clave…',
      allKinds: 'Todas las categorías',
      colName: 'Indicador',
      colValue: 'Último valor',
      colMonth: '30 días',
      colYear: 'Un año',
      colLinks: 'Enlazado',
      linked: '{count} enlaces',
      unlinked: 'Sin enlazar',
      empty: 'Todavía no hay indicadores',
      emptyCommodities: 'Todavía no hay materias primas',
      emptyHint: 'Da de alta el primero y pega su serie de precios; a partir de ahí sale todo lo demás.',
      name: 'Nombre',
      namePlaceholder: 'Acero laminado (Europa)',
      kind: 'Categoría',
      valueType: 'Qué es el valor',
      valueTypeHint: 'Un precio se lee en porcentaje; una tasa como el IPC se lee en puntos.',
      unit: 'Unidad',
      unitHint: 'Lo que va detrás del número.',
      unitPlaceholder: 'EUR/kg',
      source: 'Fuente',
      noSource: 'Sin fuente',
      badDirection: 'Qué dirección os perjudica',
      badDirectionHint: 'Que el acero suba perjudica a quien fabrica; que la electricidad baje le beneficia. Sin esto, el radar colorea al revés.',
      watchThreshold: 'Umbral de atención (%)',
      alertThreshold: 'Umbral de riesgo (%)',
      thresholdHint: 'En 30 días. Vacío usa el del módulo.',
      default: 'Por defecto',
      description: 'Descripción',
    },

    commodities: {
      title: 'Materias primas',
      subtitle: 'Lo que compráis y cuánto se mueve su precio',
    },

    indicator: {
      subtitle: '{kind} · actualizado {updated}',
      current: 'Ahora',
      trend: 'Tendencia',
      series: 'Serie',
      from: '{source} · nivel {tier}',
      noSeries: 'Todavía no hay lecturas suficientes para dibujar nada.',
      addReading: 'Añadir lectura',
      links: 'Enlaces',
      simulate: 'Simular',
      linkedTo: 'A qué afecta',
      noLinks: 'No está atado a nada vuestro',
      noLinksHint: 'Sin enlaces este indicador es información, no euros. Ata las referencias que dependen de él.',
      readings: 'Lecturas',
      noReadings: 'Todavía no hay lecturas',
      noReadingsHint: 'Añade una a mano o pega la serie entera desde una hoja de cálculo.',
      colDate: 'Fecha',
      colValue: 'Valor',
      colOrigin: 'De dónde salió',
      fromReport: 'Ver el informe',
      removeReading: '¿Quitar esta lectura?',
      removeReadingHint: 'La serie se recalcula sin ella. Queda constancia en auditoría.',
    },

    points: {
      title: 'Lecturas de {name}',
      tabOne: 'Una lectura',
      tabMany: 'Pegar una serie',
      date: 'Fecha',
      value: 'Valor {unit}',
      valuePlaceholder: '1,29',
      note: 'Nota',
      noteHint: 'De dónde sale esta cifra en concreto, si no es la fuente de siempre.',
      save: 'Guardar lectura',
      saved: 'Lectura guardada',
      paste: 'Pega dos columnas: fecha y valor',
      pasteHint: 'Tal cual salgan de la hoja de cálculo o de la web. Entiende comas y puntos decimales, y fechas en los dos formatos.',
      pastePlaceholder: '01/01/2026;1,18\n01/02/2026;1,21',
      import: 'Importar',
      imported: '{count} lecturas guardadas',
      rejected: '{count} líneas no se han podido leer',
      line: 'Línea {line}',
      reason: {
        columns: 'no tiene dos columnas',
        date: 'la fecha no se entiende',
        value: 'el valor no se entiende',
      },
    },

    // ── Enlaces ──────────────────────────────────────────────────────────────
    links: {
      title: 'Enlaces de {name}',
      lead: 'Un enlace dice qué parte del coste de una referencia explica este indicador. Sin enlaces no hay ninguna cifra en euros.',
      tabCurrent: 'Actuales',
      tabAdd: 'Añadir',
      tabSuggest: 'Proponer con IA',
      none: 'Todavía no hay enlaces',
      noneHint: 'Ata este indicador a las referencias o familias que dependen de él.',
      colTarget: 'A qué afecta',
      colWeight: 'Peso',
      colOrigin: 'Estado',
      colReason: 'Por qué',
      confirmed: 'Confirmado',
      proposed: 'Propuesto',
      confirm: 'Confirmar',
      remove: 'Quitar',
      what: 'Qué se enlaza',
      material: 'Referencia',
      materialPlaceholder: 'Busca por nombre o código…',
      category: 'Familia',
      metric: 'Cifra interna',
      metricHint: 'Para comparar el mercado con vuestra propia evolución.',
      supplierId: 'Identificador del proveedor',
      supplierHint: 'Para poder comparar a ese proveedor con este índice.',
      weight: 'Peso (0 a 1)',
      weightHint: 'Cuánto del coste de esa referencia explica este indicador. Un perfil de acero, 0,9; una válvula acabada, 0,3.',
      add: 'Añadir enlace',
      suggestLead: 'La IA propone; nadie escribe nada hasta que tú aceptas. Lo aceptado queda sin confirmar hasta que lo repasas uno a uno.',
      searchPlaceholder: 'Acota el catálogo: acero, tubo, perfil…',
      suggest: 'Proponer',
      suggestUnavailable: 'Las propuestas no están disponibles en esta instancia. Los enlaces se pueden añadir a mano igual.',
      acceptNote: 'Lo que aceptes se guarda como propuesta. La exposición dirá cuántos enlaces siguen sin confirmar.',
      accept: 'Aceptar {count}',
      accepted: '{count} enlaces creados',
      target: {
        material: 'Referencia',
        category: 'Familia',
        metric: 'Cifra interna',
        supplier: 'Proveedor',
      },
    },

    // ── Impactos ─────────────────────────────────────────────────────────────
    impacts: {
      title: 'Impactos',
      subtitle: 'Lo que se ha movido fuera, medido contra lo vuestro',
      recompute: 'Recalcular',
      recomputed: 'Recalculado: {count} impactos',
      status: 'Estado',
      direction: 'Sentido',
      risks: 'Riesgos',
      opportunities: 'Oportunidades',
      exposure: 'Compra expuesta',
      costAtRisk: 'Sobrecoste anual',
      savings: 'Ahorro anual',
      colWhat: 'Qué se ha movido',
      colChange: 'Variación',
      colCost: 'Al año',
      colReferences: 'Referencias',
      colSeverity: 'Gravedad',
      colConfidence: 'Confianza',
      overDays: 'en {count} días',
      empty: 'No hay impactos abiertos',
      emptyHint: 'Nada de lo que vigiláis se ha movido lo bastante como para costar dinero.',
    },

    impactStatus: {
      open: 'Abiertos',
      reviewed: 'Revisados',
      decided: 'Decididos',
      dismissed: 'Descartados',
      expired: 'Se deshicieron',
      all: 'Todos',
    },

    impact: {
      subtitle: '{change} en {days} días',
      explain: 'Explicar con IA',
      explainUnavailable: 'La explicación no está disponible ahora mismo. Las cifras de arriba no cambian.',
      dismiss: 'Descartar',
      dismissConfirm: '¿Descartar este impacto?',
      dismissConfirmHint: 'Deja de contar en el panel. La cifra y su rastro se quedan por si alguien pregunta.',
      decide: 'Registrar decisión',
      references: 'Referencias detrás de la cifra',
      colReference: 'Referencia',
      colShare: 'Peso',
      colSpend: 'Compra anual',
      colDelta: 'Diferencia',
      decisions: 'Decisiones tomadas',
      evidence: 'De dónde sale esto',
      state: 'Estado',
      status: 'Situación',
      raised: 'Detectado',
      reviewed: 'Revisado',
      dismissReason: 'Motivo',
      openIndicator: 'Ver el indicador',
    },

    insight: {
      what: 'Qué ha pasado',
      why: 'Por qué importa',
      action: 'Qué se puede hacer',
      overDays: 'en {count} días',
      touches: 'Afecta a {count} referencias vuestras',
      suppliers: '· {count} proveedores',
      exposure: 'Compra anual expuesta',
      annualCost: 'Efecto sobre el año',
    },

    evidence: {
      lead: 'Cada cifra que sostiene esta conclusión, y dónde mirarla.',
      open: 'Ver',
      none: 'No se guardó el rastro de este cálculo.',
      days: '{count} días',
      move: 'Movimiento del indicador',
      materials: 'Referencias afectadas',
      annualSpend: 'Compra anual expuesta',
      window: 'Ventana medida',
      confidence: 'Confianza del cálculo',
      source: 'Fuente del dato',
      unconfirmed: 'Enlaces sin confirmar',
      noData: 'Referencias sin consumo ni compras',
    },

    // ── Simulador ────────────────────────────────────────────────────────────
    simulate: {
      title: 'Simular {name}',
      lead: 'No guarda nada. Es una pregunta, y las preguntas no van a la lista de impactos.',
      custom: 'Otro porcentaje',
      verdict: 'Si {name} {change}, el año costaría {amount} {direction}.',
      more: 'más',
      less: 'menos',
      exposure: 'Compra anual expuesta',
      annualCost: 'Efecto sobre el año',
      references: 'Referencias afectadas',
      projected: 'Precio resultante',
      unconfirmed: '{count} enlaces sin confirmar',
      noData: '{count} referencias sin consumo ni compras',
      colReference: 'Referencia',
      colShare: 'Peso',
      colSpend: 'Compra anual',
      colDelta: 'Diferencia',
      suppliers: 'Con quién es la conversación',
      noLinks: 'Este indicador no está atado a ninguna referencia vuestra.',
      noLinksHint: 'Sin enlaces no hay nada que multiplicar. La respuesta es un enlace, no un número más grande.',
      close: 'Cerrar',
    },

    // ── Mercado contra empresa ───────────────────────────────────────────────
    compare: {
      title: 'Mercado y empresa',
      subtitle: 'Lo que hace el sector, al lado de lo que hacéis vosotros',
      market: 'Mercado',
      company: 'Empresa',
      chart: 'Ver',
      notLinked: 'Sin cifra interna asociada',
      link: 'Asociar',
      empty: 'Todavía no hay nada que comparar',
      emptyHint: 'Ata un indicador a una de vuestras cifras internas desde su ficha.',
      noInside: 'Todavía no hay cifras internas',
      noInsideBody: 'La columna de la derecha se llena con lo que cuentan Facturación, Presupuestos y Compras. Sin esos módulos no hay con qué comparar.',
      pairTitle: '{indicator} y {metric}',
      correlation: 'Correlación {strength} (r = {r}).',
      lag: 'El desfase que más la refuerza es de {months} meses.',
      notCausation: 'Que dos series se muevan juntas no significa que una cause la otra.',
      tooFew: 'Solo hay {count} meses comparables; hacen falta {min} para que la cifra signifique algo.',
      noCorrelation: 'No hay meses suficientes en común para calcular nada.',
    },

    // ── Proveedores ──────────────────────────────────────────────────────────
    suppliers: {
      title: 'Proveedores y mercado',
      subtitle: 'Cómo se ha movido lo que pagáis de verdad',
      method: 'Compara el precio medio ponderado de cada referencia en las dos mitades de los últimos {days} días.',
      window90: 'Tres meses',
      window180: 'Seis meses',
      window365: 'Un año',
      window730: 'Dos años',
      colSupplier: 'Proveedor y referencia',
      colBefore: 'Primera mitad',
      colAfter: 'Segunda mitad',
      colChange: 'Variación',
      colSpend: 'Compra',
      tooFew: 'Una sola compra',
      empty: 'Todavía no hay compras que comparar',
      emptyHint: 'Cada pedido de compra recibido añade líneas aquí.',
      noPurchasing: 'Compras no está instalado',
      noPurchasingBody: 'Estas cifras salen de los pedidos de compra recibidos. Sin ese módulo no llega ninguna línea.',
      caveat: 'Solo entran líneas de pedidos recibidos. Una referencia comprada una sola vez en la ventana no tiene porcentaje, y eso no es lo mismo que haber mantenido precios.',
    },

    // ── Informes ─────────────────────────────────────────────────────────────
    reports: {
      title: 'Informes',
      subtitle: 'Lo que publica el sector, leído y comparable',
      file: 'Archivar informe',
      filed: 'Informe archivado',
      searchPlaceholder: 'Título o fuente…',
      allReports: 'Todos',
      onlyRead: 'Solo los leídos',
      onlyUnread: 'Solo los pendientes',
      colTitle: 'Informe',
      colTier: 'Nivel',
      colDate: 'Fecha',
      colFindings: 'Qué se sacó',
      colConfidence: 'Confianza',
      figures: '{count} cifras',
      risks: '{count} riesgos',
      opportunities: '{count} oportunidades',
      notRead: 'Sin leer',
      duplicate: 'Repetido',
      confirmations: 'confirmado por {count}',
      empty: 'Todavía no hay informes',
      emptyHint: 'Sube el primer PDF, o pega el texto de un boletín.',
      title2: 'Título',
      titlePlaceholder: 'Informe de la industria española 2027',
      source: 'Fuente',
      noSource: 'Sin fuente',
      publishedAt: 'Fecha de publicación',
      publishedAtHint: 'La del documento, no la de hoy.',
      document: 'Documento',
      documentHint: 'PDF o fotografía. Hasta 25 MB.',
      orPaste: 'O pega el texto',
      orPasteHint: 'Para un boletín o una página web que no se puede adjuntar.',
      save: 'Archivar',
      analyseLater: 'Archivar no lo lee. La lectura con IA se pide después, desde la ficha del informe.',
    },

    report: {
      openFile: 'Abrir el documento',
      read: 'Leer con IA',
      reread: 'Volver a leer',
      readDone: 'Informe leído',
      notRead: 'Sin leer todavía',
      notReadBody: 'Nadie ha pedido que se lea. Un modelo leyendo sesenta páginas cuesta dinero, así que no pasa solo.',
      notReadHint: 'Al leerlo saldrán las cifras, los riesgos y las oportunidades, cada una con la frase de la que salió.',
      summary: 'Resumen',
      figures: 'Cifras',
      colFigure: 'Qué mide',
      colValue: 'Valor',
      colPeriod: 'Periodo',
      accept: 'Llevar a una serie',
      accepted: 'En la serie',
      risks: 'Riesgos',
      opportunities: 'Oportunidades',
      predictions: 'Previsiones',
      about: 'Ficha',
      source: 'Fuente',
      noSource: 'Sin fuente',
      published: 'Publicado',
      filed: 'Archivado',
      confidence: 'Confianza',
      corroborations: 'Lo mismo, dicho por otros',
      mentions: 'Materias primas mencionadas',
    },

    accept: {
      title: 'Llevar la cifra a una serie',
      figure: 'Cifra',
      period: 'Periodo',
      into: 'A qué indicador',
      intoHint: 'Se guardará como una lectura más de esa serie, con el informe como origen.',
      pick: 'Elige un indicador',
      confirm: 'Guardar en la serie',
      done: 'Guardado en la serie',
    },

    // ── Temas ────────────────────────────────────────────────────────────────
    topics: {
      title: 'Temas',
      subtitle: 'Lo que habéis decidido vigilar',
      new: 'Nuevo tema',
      edit: 'Editar tema',
      colName: 'Tema',
      colKind: 'Categoría',
      colImportance: 'Importancia',
      colContents: 'Contenido',
      colOwner: 'Responsable',
      contents: '{sources} fuentes · {indicators} indicadores',
      noOwner: 'Sin responsable',
      empty: 'Todavía no hay temas',
      emptyHint: 'Un tema agrupa fuentes, indicadores e informes. Empieza por los tres o cuatro que de verdad os afectan.',
      name: 'Nombre',
      namePlaceholder: 'Acero, Construcción España, Automatización…',
      description: 'Descripción',
      kind: 'Categoría',
      importance: 'Importancia',
      owner: 'Responsable',
      ownerHint: 'Un tema sin responsable es un tema que nadie actualiza.',
      archive: 'Archivar',
      restore: 'Devolver a las listas',
      restored: 'Tema devuelto a las listas',
      showArchived: 'Ver archivados',
      archiveConfirm: '¿Archivar este tema?',
      archiveConfirmHint: 'Lo que colgaba de él se queda donde está y vuelve a aparecer si lo recuperáis.',
    },

    // ── Fuentes ──────────────────────────────────────────────────────────────
    sources: {
      title: 'Fuentes',
      subtitle: 'De dónde salen las cifras, y de quién es la palabra',
      new: 'Nueva fuente',
      edit: 'Editar fuente',
      tiersTitle: 'Qué significa cada nivel',
      searchPlaceholder: 'Nombre o dirección…',
      allTiers: 'Todos los niveles',
      colName: 'Fuente',
      colTier: 'Nivel',
      colKind: 'Tipo',
      colChecked: 'Última revisión',
      overdue: '{count} días',
      foundSomething: 'Había algo',
      nothingNew: 'Nada nuevo',
      empty: 'Todavía no hay fuentes',
      emptyHint: 'Empieza por las tres o cuatro que ya leéis cada mes.',
      name: 'Nombre',
      namePlaceholder: 'INE · Índice de precios industriales',
      url: 'Dirección',
      urlPlaceholder: 'https://…',
      kind: 'Tipo',
      tier: 'Nivel de fiabilidad',
      frequency: 'Cada cuántos días revisarla',
      frequencyHint: 'Cero significa «cuando llegue algo»: un boletín no tiene calendario.',
      country: 'País',
      countryPlaceholder: 'ES',
      notes: 'Notas',
      archive: 'Archivar',
      restore: 'Devolver a las listas',
      restored: 'Fuente devuelta a las listas',
      showArchived: 'Ver archivados',
      archiveConfirm: '¿Archivar esta fuente?',
      archiveConfirmHint: 'Los indicadores y los informes que la nombran la siguen nombrando.',
    },

    // ── Alertas ──────────────────────────────────────────────────────────────
    alerts: {
      title: 'Alertas',
      subtitle: 'Qué merece interrumpir a alguien',
      new: 'Nueva alerta',
      edit: 'Editar alerta',
      quietTitle: 'Cómo se evita el ruido',
      quietBody: 'Una regla avisa UNA vez por cada movimiento, no una vez por noche. Después se calla los días que le digas, incluso sobre otro asunto: veinte avisos por lo mismo son veinte avisos que nadie lee.',
      colName: 'Alerta',
      colKind: 'Vigila',
      colWho: 'Avisa a',
      colLast: 'Último aviso',
      colState: 'Estado',
      neverFired: 'Nunca ha saltado',
      on: 'Activa',
      off: 'Parada',
      empty: 'Todavía no hay alertas',
      emptyHint: 'Sin alertas el módulo sigue funcionando: hay que entrar a mirarlo.',
      name: 'Nombre',
      namePlaceholder: 'El cobre sube más de un 5 %',
      kind: 'Qué vigila',
      indicator: 'Indicador',
      indicatorHint: 'Vacío vigila todos.',
      indicatorSupplierHint: 'El índice contra el que se compara al proveedor. Hace falta uno.',
      anyIndicator: 'Cualquiera',
      thresholdPct: 'A partir de qué porcentaje',
      thresholdEur: 'A partir de cuántos euros al año',
      window: 'En cuántos días',
      severity: 'Gravedad',
      quiet: 'Días de silencio',
      quietHint: 'Después de avisar, se calla este tiempo.',
      who: 'A qué roles avisa',
      whoHint: 'Roles, no personas: una alerta dirigida a alguien deja de funcionar la semana que se va.',
      whoPlaceholder: 'admin, manager',
      active: 'Activa',
      removeConfirm: '¿Quitar esta alerta?',
      removeConfirmHint: 'Deja de avisar. Lo que ya avisó se queda en las notificaciones.',
      sentence: {
        price: 'Cuando un indicador se mueva más de un {threshold} % en {days} días. Silencio de {quiet} días.',
        exposure: 'Cuando un impacto abierto pase de {threshold} € al año. Silencio de {quiet} días.',
        supplier: 'Cuando un proveedor suba más de {threshold} puntos por encima de su índice. Silencio de {quiet} días.',
        report: 'Cuando llegue un informe nuevo sobre el tema, mirando los últimos {days} días. Silencio de {quiet} días.',
        trend: 'Cuando un indicador cambie de dirección con más de un {threshold} %. Silencio de {quiet} días.',
      },
    },

    alertKinds: {
      price: 'Un precio',
      priceHint: 'Salta cuando un indicador se mueve más de lo que digas en la ventana que digas.',
      exposure: 'Lo que cuesta',
      exposureHint: 'Salta por el dinero, no por el porcentaje: un 2 % sobre la compra principal es más que un 12 % sobre algo que compráis dos veces al año.',
      supplier: 'Un proveedor',
      supplierHint: 'Salta cuando un proveedor sube por encima del índice con el que se le compara. Necesita ese índice enlazado.',
      report: 'Un informe nuevo',
      reportHint: 'Salta cuando aparece algo nuevo sobre un tema.',
      trend: 'Un cambio de tendencia',
      trendHint: 'Salta cuando el mes y el año dejan de ir en la misma dirección.',
    },

    // ── Decisiones ───────────────────────────────────────────────────────────
    decisions: {
      title: 'Decisiones',
      subtitle: 'Qué se decidió, y si salió bien',
      new: 'Registrar decisión',
      allKinds: 'Todas',
      onlyPending: 'Solo las que toca valorar',
      colWhat: 'Decisión',
      colKind: 'Tipo',
      colWhen: 'Fecha',
      colOutcome: 'Resultado',
      reviewed: 'Valorada',
      dueNow: 'Toca valorarla',
      noReview: 'Sin fecha de revisión',
      empty: 'Todavía no hay decisiones',
      emptyHint: 'Cuando dirección decida algo a partir de estos datos, apúntalo aquí: es la única forma de saber después si funcionó.',
      about: 'Sobre {name} {change} · {amount} al año',
      what: 'Qué se decide',
      whatPlaceholder: 'Subir la tarifa de estructura un 4 %',
      kind: 'Tipo',
      reason: 'Por qué',
      reasonHint: 'Lo que se sabía ese día. Dentro de un año esto es lo que se lee.',
      expected: 'Qué se espera que pase',
      expectedHint: 'Contra esto se valorará después.',
      reviewAt: 'Cuándo volver a mirarlo',
      reviewAtHint: 'Sin fecha, nadie vuelve.',
      willClose: 'Registrar la decisión cierra también el impacto que la motivó.',
      record: 'Registrar',
      recorded: 'Decisión registrada',
    },

    decisionKinds: {
      update_prices: 'Actualizar tarifas',
      negotiate_supplier: 'Negociar con el proveedor',
      buy_ahead: 'Comprar por adelantado',
      change_material: 'Cambiar de material',
      review_quotes: 'Revisar presupuestos',
      hold_prices: 'Mantener precios',
      raise_minimum: 'Subir el mínimo de stock',
      hedge: 'Cubrirse',
      other: 'Otra',
    },

    decision: {
      subtitle: '{kind} · {when}',
      why: 'Por qué se decidió',
      noReason: 'No se escribió el motivo.',
      expected: 'Qué se esperaba',
      outcome: 'Cómo salió',
      noOutcome: 'Todavía no se ha valorado.',
      howItWent: 'Qué pasó de verdad',
      howItWentHint: 'Con números si los hay. Es lo que permite saber si el módulo sirve.',
      recordOutcome: 'Guardar el resultado',
      outcomeRecorded: 'Resultado guardado',
      basedOn: 'Con qué se decidió',
      about: 'Ficha',
      kind: 'Tipo',
      decidedAt: 'Fecha',
      decidedBy: 'Quién',
      reviewAt: 'Revisión',
      openImpact: 'Ver el impacto',
    },

    // ── Informe de dirección ─────────────────────────────────────────────────
    briefing: {
      title: 'Informe de dirección',
      subtitle: 'Lo que ha pasado, lo que cuesta y qué revisar',
      weekly: 'Informe semanal',
      meeting: 'Briefing de reunión',
      write: 'Redactar con IA',
      unavailable: 'La redacción no está disponible. Las cifras de abajo son el informe igual.',
      forDirection: 'Para dirección',
      situation: 'Situación',
      risks: 'Riesgos',
      opportunities: 'Oportunidades',
      suppliers: 'Proveedores',
      actions: 'Qué hacer esta semana',
      noProse: 'Sin redacción',
      noProseBody: 'El modelo no ha podido redactar. Las cifras están completas debajo.',
      risksCount: 'Riesgos abiertos',
      opportunitiesCount: 'Oportunidades',
      exposure: 'Compra expuesta',
      costAtRisk: 'Sobrecoste anual',
      committed: 'Comprometido a proveedores',
      commodities: 'Materias primas',
      economy: 'Economía y sector',
      openRisks: 'Riesgos abiertos',
      colWhat: 'Qué',
      colChange: 'Variación',
      colCost: 'Al año',
      colReferences: 'Referencias',
      gaps: 'Qué no cubre este informe',
      gapUnlinked: '{count} indicadores no están atados a nada vuestro: se vigilan, pero no producen ninguna cifra en euros.',
      gapStale: '{count} indicadores llevan tiempo sin actualizarse.',
      gapCatalogue: 'No hay catálogo de materiales espejado: sin Almacén no se puede convertir un movimiento de precio en euros.',
      gapQuotes: {
        not_available: 'El margen de los presupuestos abiertos no entra aquí: los costes de un presupuesto no aceptado no salen del módulo de Presupuestos, así que este no los conoce.',
        module_absent: 'El margen de los presupuestos abiertos no entra aquí: Presupuestos no está instalado.',
      },
    },

    chart: {
      title: 'Serie',
      empty: 'Sin datos suficientes',
      notZeroed: 'El eje no empieza en cero',
    },

    // ── Lo que este módulo pone en la ficha de otro ──────────────────────────
    related: {
      material: 'Mercado',
      supplier: 'Precios y mercado',
    },

    // ── Widgets del inicio ───────────────────────────────────────────────────
    dashboard: {
      risks: 'Riesgos de mercado',
      risksHelp: 'Cuántos movimientos de fuera están costando dinero ahora mismo.',
      costAtRisk: 'Sobrecoste anual',
      costAtRiskHelp: 'Lo que los movimientos abiertos añaden al coste de un año, a consumo actual.',
      opportunities: 'Oportunidades',
      opportunitiesHelp: 'Cuántos movimientos de fuera están a favor: materias primas o energía a la baja.',
      exposure: 'Compra expuesta',
      exposureHelp: 'La compra anual que pasa por las referencias afectadas por algo que se ha movido.',
      watched: 'Factores vigilados',
      watchedHelp: 'Cuántos indicadores están en el radar. Avisa en ámbar si alguno lleva tiempo sin actualizarse.',
      moved: 'Lo que se mueve',
      movedHelp: 'Los factores fuera de lo normal, con su variación. Estira la tarjeta y caben más.',
      reports: 'Informes nuevos',
      reportsHelp: 'Lo que ha llegado en las dos últimas semanas, con el nivel de su fuente.',
    },

    // ── Avisos ───────────────────────────────────────────────────────────────
    notifications: {
      category: 'Mercados',
      price: {
        title: 'Movimiento en {indicator}',
        body: '{indicator} se ha movido un {change} % en {days} días.',
      },
      trend: {
        title: 'Cambio de tendencia en {indicator}',
        body: '{indicator} va al {change} % en el mes y al {year} % en el año: ha cambiado de dirección.',
      },
      exposure: {
        title: 'Sobrecoste por {indicator}',
        body: '{indicator} añade unos {cost} € al año, sobre {materials} referencias.',
      },
      supplier: {
        title: '{supplier} sube por encima del mercado',
        body: '{supplier} va {deviation} puntos por encima de {indicator}.',
      },
      report: {
        title: 'Informes nuevos',
        body: 'Han llegado {count} informes. El último: {title}.',
      },
      weekly: {
        title: 'Informe de mercado',
        body: '{risks} riesgos y {opportunities} oportunidades. Sobrecoste anual estimado: {cost} €.',
      },
    },

    jobs: {
      assess: 'Recalcular el mercado y evaluar las alertas',
      briefing: 'Informe semanal de mercado',
    },

    assistant: {
      recordingDecision: 'Registrando la decisión',
    },
  },

  // ── Lo que este módulo aporta a las pantallas de la plataforma ─────────────

  permissions: {
    markets: {
      read: 'Ver el mercado: radar, indicadores, informes y fuentes',
      exposure: 'Ver lo que el mercado nos cuesta, en euros',
      write: 'Mantener indicadores, series, enlaces, fuentes e informes',
      decide: 'Registrar decisiones, cerrar impactos y configurar alertas',
    },
  },

  settings: {
    ns: {
      markets: {
        title: 'Mercados',
        subtitle: 'Qué se considera mucho en esta empresa',
        watchThreshold: {
          label: 'Movimiento que merece mirarse (%, en 30 días)',
          help: 'Por debajo de esto un movimiento es ruido y no se registra nada. Cada indicador puede llevar el suyo; este es el del resto.',
        },
        alertThreshold: {
          label: 'Movimiento que se considera mucho (%, en 30 días)',
          help: 'A partir de aquí el radar lo pinta como riesgo y sube la gravedad de lo que cuesta.',
        },
        impactFloor: {
          label: 'Coste anual mínimo para registrar un impacto (€)',
          help: 'Un movimiento que cuesta menos de esto al año es cierto y no es noticia. Sin este suelo, la lista de lo que necesita atención se llena de filas sobre las que nadie va a actuar.',
        },
        highCost: {
          label: 'Coste anual que hace importante un impacto (€)',
          help: 'Lo que separa algo que hay que vigilar de algo que se lleva a una reunión.',
        },
        criticalCost: {
          label: 'Coste anual que hace crítico un impacto (€)',
          help: 'Por encima de esto se marca como crítico y quien esté en la alerta se entera.',
        },
        weeklyBriefing: {
          label: 'Mandar el informe de mercado los lunes',
          help: 'Un resumen de lo que se ha movido, lo que cuesta y qué mirar. Desactívalo si preferís entrar a la pantalla.',
        },
      },
    },
  },

  audit: {
    actions: {
      markets: {
        topic_created: 'Tema creado',
        topic_updated: 'Tema modificado',
        topic_archived: 'Tema archivado',
        topic_restored: 'Tema devuelto a las listas',
        source_created: 'Fuente creada',
        source_updated: 'Fuente modificada',
        source_checked: 'Fuente revisada',
        source_archived: 'Fuente archivada',
        source_restored: 'Fuente devuelta a las listas',
        indicator_created: 'Indicador creado',
        indicator_updated: 'Indicador modificado',
        indicator_archived: 'Indicador archivado',
        indicator_restored: 'Indicador devuelto a las listas',
        point_recorded: 'Lectura registrada',
        point_removed: 'Lectura quitada',
        series_imported: 'Serie importada',
        link_created: 'Enlace creado',
        link_updated: 'Enlace modificado',
        link_removed: 'Enlace quitado',
        links_suggested: 'Enlaces propuestos por IA aceptados',
        report_filed: 'Informe archivado',
        report_updated: 'Informe modificado',
        report_archived: 'Informe retirado',
        impact_reviewed: 'Impacto revisado',
        impact_dismissed: 'Impacto descartado',
        alert_created: 'Alerta creada',
        alert_updated: 'Alerta modificada',
        alert_removed: 'Alerta quitada',
        decision_recorded: 'Decisión registrada',
        decision_updated: 'Decisión modificada',
        decision_reviewed: 'Resultado de la decisión registrado',
      },
    },
  },

  assistant: {
    openers: {
      markets: {
        'whats-changed': '¿Qué ha cambiado que pueda afectarnos?',
        'biggest-risk': '¿Cuál es ahora mismo nuestro mayor riesgo de mercado?',
        'supplier-above-market': '¿Hay algún proveedor subiendo por encima del mercado?',
        simulate: '¿Cuánto nos costaría otra subida de nuestra materia prima principal?',
      },
    },
  },
}
